/**
 * Centralized role normalization (Feb 27, 2026).
 *
 * Single source of truth for converting any role string (canonical key
 * OR display name) to its canonical lowercase snake_case key.
 *
 * Why this exists:
 * The backend historically stored `user.role` as a display name (e.g.
 * "Tax Search Team Leader") in some code paths and as the canonical
 * key (e.g. "tax_search_team_leader") in others. Read-time normalizers
 * exist in both /auth/login and /auth/me, but during the brief hydration
 * window — or when components read directly from localStorage —
 * `user.role` can still surface as the display-name form.
 *
 * Every role-gate in the UI must run the role through this helper
 * before comparing it to a hardcoded canonical key. Strict
 * `[...].includes(user?.role)` checks silently fail when the role
 * arrives as a display name; using `[...].includes(nRole(user?.role))`
 * is safe in both forms.
 *
 * Usage:
 *   import { nRole } from '../utils/roleNormalizer';
 *   const isTeamLeader = ['tax_search_team_leader', 'title_search_team_leader']
 *                          .includes(nRole(user?.role));
 */

// Must match server.py normalize_role_to_key DISPLAY_TO_KEY mapping.
export const DISPLAY_TO_KEY = Object.freeze({
  'Admin': 'admin',
  'Super Admin': 'admin',
  'Order Desk': 'order_desk',
  'Tax Processor': 'processor',
  'Search Processor': 'search_processor',
  'Tax Search Team Leader': 'tax_search_team_leader',
  'Title Search Team Leader': 'title_search_team_leader',
  'Jurisdiction Manager': 'jurisdiction_manager',
  'QC Specialist': 'qc_specialist',
  'IT Support': 'it_support',
  'IT Manager': 'it_manager',
  'HR': 'hr',
  'HR Specialist': 'hr',
  'HR Manager': 'hr_manager',
  'Accounting': 'accounting',
});

/**
 * Normalize any role string to its canonical lowercase snake_case key.
 * Falls back to the raw string when no mapping exists (covers legacy
 * roles and already-canonical inputs).
 *
 * @param {string|null|undefined} r
 * @returns {string} canonical role key (or empty string)
 */
export const nRole = (r) => DISPLAY_TO_KEY[r] || r || '';
